import { createUserSiteCoreKeys } from "../siteCore/SiteCoreKeys";

export const UserRoleKey = {
  userEmail: "userEmail",
  firstName: "firstName",
  lastName: "lastName",
  activeDirectoryId: "activeDirectoryId",
  role: "role",
  clientPortalRole: "clientPortalRole",
  country: "country",
  servicesOffice: "servicesOffice",
  hasAccessToPowerBIReport: "hasAccessToPowerBIReport",
  roleType: "roleType",
} as const;

export const USER_ROLE_TYPES = {
  CIRRUS_P3_AND_CANDIDATE_PORTAL: "CIRRUS_P3_AND_CANDIDATE_PORTAL",
  CLIENT_PORTAL: "CLIENT_PORTAL",
  ALL: "ALL",
};

export const createUserRoleFormDefaultValues = {
  [UserRoleKey.userEmail]: "",
  [UserRoleKey.firstName]: "",
  [UserRoleKey.lastName]: "",
  [UserRoleKey.activeDirectoryId]: "",
  [UserRoleKey.role]: [] as string[],
  [UserRoleKey.clientPortalRole]: "",
  [UserRoleKey.country]: [],
  [UserRoleKey.servicesOffice]: [],
  [UserRoleKey.hasAccessToPowerBIReport]: false,
  [UserRoleKey.roleType]: USER_ROLE_TYPES.CIRRUS_P3_AND_CANDIDATE_PORTAL,
};

export type CreateUserRoleFormValues =
  typeof createUserRoleFormDefaultValues;

export interface AddedReport {
  reportId: string;
  reportName?: string;
  geoType?: string | number;
  salesRegion: string[];
  salesMarket: string[];
}

export const UserDetailKey = {
  preferredName: "preferredName",
  userEmail: "userEmail",
  affliateEmailId: "affliateEmailId",
  userCountry: "userCountry",
  homeOffice: "homeOffice",
  applicationLanguage: "applicationLanguage",
  timeZone: "timeZone",
  userStatus: "userStatus",
  dateFormat: "dateFormat",
} as const;

export const createUserUserDetailFormDefaultValues = {
  [UserDetailKey.preferredName]: "",
  [UserDetailKey.userEmail]: "",
  [UserDetailKey.affliateEmailId]: "",
  [UserDetailKey.userCountry]: "",
  [UserDetailKey.homeOffice]: "",
  [UserDetailKey.applicationLanguage]: "",
  [UserDetailKey.timeZone]: "",
  [UserDetailKey.userStatus]: createUserSiteCoreKeys.statusActive,
  [UserDetailKey.dateFormat]: "",
};

export const CoachDetailKey = {
  bioData: "bioData",
  expertise: "expertise",
  linkedInId: "linkedInId",
  coachingLanguages: "coachingLanguages",
  timeZone: "timeZone",
  serviceAccountName: "serviceAccountName",
  availableDays: "availableDays",
  workingHoursStartTime: "workingHoursStartTime",
  workingHoursEndTime: "workingHoursEndTime",
} as const;

export const createUserCoachDetailFormDefaultValues = {
  [CoachDetailKey.bioData]: "",
  [CoachDetailKey.expertise]: "",
  [CoachDetailKey.linkedInId]: "",
  [CoachDetailKey.coachingLanguages]: "",
  [CoachDetailKey.timeZone]: "",
  [CoachDetailKey.serviceAccountName]: "",
  [CoachDetailKey.availableDays]: [],
  [CoachDetailKey.workingHoursStartTime]: "",
  [CoachDetailKey.workingHoursEndTime]: "",
};

export const ADD_USER_TABS = {
  ROLE_DETAIL: 0,
  USER_DETAIL: 1,
  COACH_DETAIL: 2,
  POWER_BI_REPORTS: 3,
};

export interface UserCountryType {
  id: string;
  title: string;
}

export interface ServiceOfficeType {
  rightOfficeId: string;
  rightOfficeName: string;
  countryCode: string;
  countryCodeName?: string;
  officeId: string;
  officeName?: string;
}

export interface ServiceOfficeResultType {
  id: string;
  name: string;
  countryCode: string;
}

export interface ServiceAccountType {
  id: string;
  serviceAccountName: string;
}

export interface TimeZoneType {
  id: string;
  title: string;
}

export interface CandidatePortalRole {
  roleId?: string;
  role?: string;
}

export interface MarketRegions {
  salesRegionId: string;
  salesRegionName: string;
  id: string;
  name: string;
}

export interface PowerBiReportsData {
  reportId: string;
  reportName: string;
  globalIndicator: string;
  regionMarket: MarketRegions[];
}

export interface ViewUserDataType {
  activeDirectoryId?: string;
  isActive?: boolean;
  id?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  staffRoleId?: string;
  staffType?: string;
  emailAddress?: string;
  primaryContactNo?: string;
  bio?: string;
  expertise?: string;
  language?: string;
  socialIdDetails?: string;
  availableTimeSlots?: string;
  timeZone?: string;
  countryCode?: string;
  rightOfficeId?: string;
  rightOffice?: string;
  officeId?: string;
  officeName?: string;
  applicationLanguageCode?: string;
  affiliateEmailAddress?: string;
  ianaTimeZoneIdentifier?: string;
  serviceAccountId?: string;
  isEnterpriseAdmin?: boolean;
  clientPortalAdminRoleId?: string;
  clientPortalAdminRole?: string;

  adminUserRoles?: {
    candidatePortalRole?: CandidatePortalRole[];
    clientPortalRole?: {
      roleId?: string;
      role?: string;
    };
    enterpriseRole?: {
      roleId?: string;
      role?: string;
    };
    serviceOffices?: ServiceOfficeType[];
  };
  schedulingAvailability?: {
    tzid?: string;
    weekly_periods?: {
      day?: string;
      start_time?: string;
      end_time?: string;
    }[];
  };
  countryName?: string;
  timeZoneName?: string;
  serviceAccountName?: string;
  ianaTimeZoneIdentifierName?: string;
  hasAccessToPowerBIReport: boolean;
  reports?: {
    comment: string;
    powerBIReport: PowerBiReportsData[];
  };
  userDateFormat: {
    name: string;
    id: string;
  };
}

export interface CoachTimeZoneType {
  id: string;
  ianaTimeZoneIdentifier: string;
}

export const defaultServiceOffice = {
  ServiceOfficeId: "0",
  ServiceOfficeName: "unknown",
};

export const PowerBiReportsDetailKey = {
  userEmail: "userEmail",
  firstName: "firstName",
  lastName: "lastName",
  reports: "reports",
  comments: "comments",
};

export const createUserPowerBiReportsDefaultValues = {
  [PowerBiReportsDetailKey.userEmail]: "",
  [PowerBiReportsDetailKey.firstName]: "",
  [PowerBiReportsDetailKey.lastName]: "",
  [PowerBiReportsDetailKey.reports]: [],
  [PowerBiReportsDetailKey.comments]: "",
};

export interface TransformedRegionMarketData {
  name: string;
  value: string;
  children: {
    name: string;
    value: string;
  }[];
}

export interface Office {
  officeCode: string;
  countryCode: string;
  id: string;
  name: string;
}
